/**
 * O cartão do próprio número de WhatsApp (história 8.1, FR-71, FR-38).
 *
 * **Desde a 11.2 ele mora em Integrações → WhatsApp** (`/integracoes`), e não mais na tela
 * `/whatsapp`, que ficou só com as conversas e leva até aqui pelo link "Meu número". É a porta de
 * entrada de todas elas — **cada um conecta o próprio chip sozinho, sem passar pelo Admin** (FR-71).
 *
 * Três estados visíveis, e eles não competem:
 *
 * * **Desconectado** — o selo cinza, a explicação e o botão "Conectar". É o estado de quem nunca
 *   pareou e o de quem caiu; quando já houve um número, ele continua na tela, porque é ele que diz
 *   a quem vai reconectar qual chip estava ali.
 * * **Pareando** — o QR desta tentativa e o aviso de que ele expira. É o único estado em que a tela
 *   **espera por fora**: quem confirma a conexão não é o clique, e sim o webhook do provedor, e por
 *   isso — e só aqui — a consulta relê sozinha (a justificativa inteira está em `api.ts`).
 * * **Conectado** — o selo verde, o número que atendeu e o botão "Desconectar", atrás de uma
 *   confirmação.
 *
 * **O `pareando` tem três formas, e as três precisam existir na tela.** Com o QR vivo, ele é o
 * código mais a instrução de ler. Com o QR **vencido**, ele vira "peça outro" — e o relógio da
 * releitura para junto, porque não há mais nada a esperar. E com o QR **ausente** — quem recarregou
 * a página no meio do pareamento —, ele diz que o código não está mais aqui e oferece gerar outro:
 * sem esse terceiro caso, a tela mandava ler um código sobre uma área vazia. Nenhum deles é um erro
 * do serviço; os três são o mesmo estado visto de momentos diferentes.
 *
 * **O QR anterior fica na tela enquanto o próximo não responde.** O TanStack zera o `data` da
 * mutação assim que ela recomeça, então ler `pareamento.data` direto apagaria o código que a pessoa
 * está mirando com o celular na mão no instante em que ela toca em "Gerar outro QR" — e, se a
 * segunda chamada falhasse com `503`, ela ficaria sem código nenhum e sem forma de voltar. Guardar
 * o último código **bem-sucedido** no estado da tela é o que faz a falha ser só uma mensagem em
 * cima do que já estava ali.
 *
 * **O QR é desenhado do jeito que o provedor o entrega, e nada é inventado.** Quando ele vem como
 * imagem embutida (`data:image/...`), a tela o mostra como imagem; quando vem como o texto do
 * código, ela o mostra como texto selecionável, com a instrução de gerar o QR onde a pessoa
 * preferir. Adotar uma biblioteca de QR para "melhorar" o segundo caso seria escrever um desenho
 * que ninguém conferiu contra o provedor real — e a conta do uazapi ainda não existe.
 *
 * **A recusa do serviço vai à tela verbatim**, em `role="alert"`. Ela vem do envelope único
 * (`whatsapp_indisponivel` quando o provedor não responde ou quando o ambiente ainda não tem a
 * credencial dele, `nao_autorizado` quando a permissão foi retirada), e reescrevê-la aqui seria uma
 * segunda redação da mesma frase — a que a pessoa lê seria a que ninguém revisou. **A falha da
 * releitura tem alerta próprio**, e não silêncio: enquanto o QR está na tela a consulta roda
 * sozinha, e um erro mudo ali deixaria a pessoa esperando uma confirmação que já não vem.
 *
 * **Desconectar passa por `Dialogo`**, como as ações destrutivas do resto do CRM: derrubar a sessão
 * interrompe a conversa com todos os clientes daquele Vendedor, e não há desfazer — o caminho de
 * volta é ler outro QR com o celular na mão.
 *
 * **E este é o primeiro uso de `Dialogo` fora de `modulos/negocios`**, o que é deliberado e não
 * descuido: ele foi extraído da ficha do Negócio na 4.4 justamente para não haver uma segunda cópia
 * — e o que não pode divergir nele é a acessibilidade (o foco entra, `Esc` fecha, `aria-modal` diz
 * ao leitor de tela que o resto da página está fora de alcance). Um diálogo próprio aqui teria de
 * repetir isso, e a cópia envelheceria calada. Mudá-lo de casa (para um `modulos/comum`, digamos) é
 * uma refatoração de outro dia: ela mexeria em quatro arquivos que esta história não toca.
 *
 * **Paridade de celular (NFR-1).** Não há função exclusiva do desktop e não há árvore alternativa:
 * o selo, o QR, o número e os botões existem nas duas larguras, e o que muda é só como eles se
 * empilham. Sem `@media`.
 */

import { type ReactNode, useEffect, useState } from 'react'

import { ErroDaApi, MENSAGEM_DE_ERRO_DESCONHECIDO } from '@/api/erros'
import { Dialogo } from '@/modulos/negocios/Dialogo'
import { useConectarMeuNumero, useDesconectarMeuNumero, useMeuNumero } from './api'
import './meu-numero.css'

/** `id` do título — é ele que nomeia a `<section>` para o leitor de tela. */
const ID_DO_TITULO = 'meu-numero-titulo'

export const TITULO = 'Meu número de WhatsApp'

export const SELO_DESCONECTADO = 'Desconectado'
export const SELO_PAREANDO = 'Pareando'
export const SELO_CONECTADO = 'Conectado'

export const CONECTAR = 'Conectar'
export const CONECTANDO = 'Pedindo o QR…'
export const NOVO_QR = 'Gerar outro QR'
export const DESCONECTAR = 'Desconectar'
export const DESCONECTANDO = 'Desconectando…'

export const TITULO_DA_CONFIRMACAO = 'Desconectar o seu número?'
export const SUBTITULO_DA_CONFIRMACAO =
  'As conversas param de chegar até você conectar de novo pelo QR.'

const CARREGANDO = 'Carregando a situação do seu número…'

/** A nota do cabeçalho do cartão (protótipo v2): o que ele é, para qualquer estado. */
export const NOTA_DO_CARTAO =
  'Conecte o seu próprio número lendo um QR com o WhatsApp do celular. O número é só seu: ' +
  'ninguém mais o vê nem o desconecta.'

const AJUDA_DESCONECTADA =
  'Toque em Conectar: o código aparece aqui, e você o lê no WhatsApp do celular, em ' +
  'Aparelhos conectados. Não depende do administrador.'

const AJUDA_PAREANDO =
  'Abra o WhatsApp no celular, vá em Aparelhos conectados e leia o código abaixo. O QR expira em ' +
  'poucos segundos — se ele vencer, peça outro.'

/**
 * O QR desta tentativa venceu sem ninguém ler. Não é erro de nada: o código do WhatsApp gira por
 * construção, e pedir outro é o gesto normal.
 */
export const AJUDA_QR_EXPIRADO =
  'O código expirou antes de ser lido. Peça outro e leia com o celular já na mão.'

/**
 * Quem recarregou a página no meio do pareamento. O código vivia só nesta tela — ele não é guardado
 * em lugar nenhum, e não poderia ser: é credencial de sessão, e gira em segundos.
 */
export const AJUDA_SEM_QR =
  'O código desta tentativa não está mais nesta tela. Gere outro para continuar de onde parou.'

/** O aviso do estado conectado (protótipo v2): o que o celular faz aparecer aqui. */
export const AVISO_CONECTADO =
  'O que você digita no celular também aparece no CRM e conta como contato. Reação é reação — ' +
  'nunca vira mensagem.'

/** "pareado em dd/mm/aaaa" ao lado do número, no fuso de São Paulo. */
function pareadoEm(instante: string): string {
  return `pareado em ${new Date(instante).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}`
}

/** O prefixo que o provedor usa quando entrega o QR já como imagem embutida. */
const PREFIXO_DE_IMAGEM = 'data:image'

/** O código desta tentativa e até quando ele vale — o que a tela guarda de um pareamento. */
interface QrNaTela {
  codigo: string
  expiraEm: string
}

/** A mensagem do serviço quando ela existe; a genérica quando a falha nem chegou a ter uma. */
function mensagemDe(falha: unknown): string {
  return falha instanceof ErroDaApi ? falha.message : MENSAGEM_DE_ERRO_DESCONHECIDO
}

function Cartao({ situacao, children }: { situacao?: string; children: ReactNode }) {
  return (
    <section className="meu-numero" aria-labelledby={ID_DO_TITULO}>
      <div className="meu-numero__cartao" data-situacao={situacao}>
        <div className="meu-numero__cabecalho">
          <h2 className="meu-numero__titulo" id={ID_DO_TITULO}>
            {TITULO}
          </h2>
          <p className="meu-numero__nota">{NOTA_DO_CARTAO}</p>
        </div>
        <div className="meu-numero__corpo">{children}</div>
      </div>
    </section>
  )
}

export function MeuNumero() {
  const pareamento = useConectarMeuNumero()
  const desconexao = useDesconectarMeuNumero()

  // O QR vive **na tela**, e não no cache nem no `data` da mutação: ele é o resultado desta
  // tentativa, gira em segundos e some quando a tela sai. No cache, reapareceria morto na próxima
  // visita; lido de `pareamento.data`, sumiria no clique que pede o próximo.
  const [qr, definirQr] = useState<QrNaTela | undefined>(undefined)
  const [confirmando, definirConfirmando] = useState(false)

  /*
   * O relógio da expiração — e ele guarda um **instante**, e não o booleano "expirou".
   *
   * Ele existe porque a tela precisa mudar sozinha quando o código vence: a releitura periódica
   * para ali (é ela quem descobre que não há mais nada a esperar), e sem um despertador próprio o
   * último render seria o de "leia o código abaixo" sobre um QR já morto, sem nada mais
   * acontecendo na página para corrigi-lo.
   *
   * Guardar o instante, e não o booleano, é o que mantém `expirado` **derivado**: com um booleano,
   * cada QR novo exigiria zerá-lo dentro do efeito — um `setState` síncrono no corpo, que é
   * exatamente a cascata de renders que o repositório recusa. Aqui o efeito só agenda, e quem
   * decide é a comparação lá embaixo.
   */
  const [agora, definirAgora] = useState(() => Date.now())
  useEffect(() => {
    if (qr === undefined) {
      return
    }
    // `Math.max(..., 0)` cobre o QR que chega já vencido — improvável, e mesmo assim o disparo no
    // próximo tick é o que faz a tela dizer "peça outro" em vez de mandar ler um código morto.
    const relogio = setTimeout(
      () => {
        definirAgora(Date.now())
      },
      Math.max(Date.parse(qr.expiraEm) - Date.now(), 0),
    )
    return () => {
      clearTimeout(relogio)
    }
  }, [qr])

  const expirado = qr !== undefined && Date.parse(qr.expiraEm) <= agora

  // `null` desliga a releitura: sem QR na tela, ou com ele vencido, não há mais nada a esperar.
  const consulta = useMeuNumero(qr === undefined || expirado ? null : qr.expiraEm)
  const numero = consulta.data

  if (consulta.isPending) {
    return (
      <Cartao>
        <p className="meu-numero__ajuda">{CARREGANDO}</p>
      </Cartao>
    )
  }

  // A leitura recusada é o `403` de quem teve `conectar_proprio_numero` retirada. Ela ocupa a tela
  // inteira de propósito: sem saber a situação, nenhum botão desta tela tem o que fazer.
  if (numero === undefined) {
    return (
      <Cartao>
        <p className="meu-numero__erro" role="alert">
          {mensagemDe(consulta.error)}
        </p>
      </Cartao>
    )
  }

  const situacao = numero.situacao
  const pareando = situacao === 'pareando'
  const qrNaTela = pareando && qr !== undefined && !expirado ? qr.codigo : undefined

  const selo =
    situacao === 'conectado' ? SELO_CONECTADO : pareando ? SELO_PAREANDO : SELO_DESCONECTADO

  // As três formas do `pareando`, na ordem em que elas acontecem: o código vivo, o código vencido e
  // o código que não veio junto com a página recarregada. Conectado não tem ajuda: tem o aviso.
  const ajuda =
    situacao === 'conectado'
      ? null
      : !pareando
        ? AJUDA_DESCONECTADA
        : qrNaTela !== undefined
          ? AJUDA_PAREANDO
          : qr === undefined
            ? AJUDA_SEM_QR
            : AJUDA_QR_EXPIRADO

  // "Gerar outro QR" enquanto pareia, e não um segundo botão: é o **mesmo** ato — pedir o código —
  // e o rótulo diz o que ele faz agora. Dois botões dariam à pessoa uma escolha que não existe.
  const rotuloDoConectar = pareamento.isPending ? CONECTANDO : pareando ? NOVO_QR : CONECTAR

  return (
    <section className="meu-numero" aria-labelledby={ID_DO_TITULO}>
      <div className="meu-numero__cartao" data-situacao={situacao}>
        <div className="meu-numero__cabecalho">
          <h2 className="meu-numero__titulo" id={ID_DO_TITULO}>
            {TITULO}
          </h2>
          <p className="meu-numero__nota">{NOTA_DO_CARTAO}</p>
        </div>

        <div className="meu-numero__corpo">
          {/* A situação, com o ponto colorido do protótipo: a palavra é o que o leitor anuncia. */}
          <p className="meu-numero__selo" data-situacao={situacao}>
            {selo}
          </p>

          {/* O número que atendeu, em fonte mono, e desde quando. Ele continua na tela depois de
              uma queda, e é isso que diz a quem vai reconectar qual chip era este. */}
          {numero.numero === null ? null : (
            <p className="meu-numero__numero">
              <code>{numero.numero}</code>
              {situacao === 'conectado' && numero.atualizado_em ? (
                <span className="meu-numero__pareado">{pareadoEm(numero.atualizado_em)}</span>
              ) : null}
            </p>
          )}

          {ajuda === null ? (
            <p className="meu-numero__aviso">{AVISO_CONECTADO}</p>
          ) : (
            <p className="meu-numero__ajuda">{ajuda}</p>
          )}

          {qrNaTela === undefined ? null : (
            <div className="meu-numero__qr">
              {qrNaTela.startsWith(PREFIXO_DE_IMAGEM) ? (
                <img
                  className="meu-numero__qr-imagem"
                  src={qrNaTela}
                  alt="Código QR para conectar o seu número no WhatsApp"
                />
              ) : (
                /* O código como texto selecionável: é o que o provedor entregou, e a tela não
                   inventa um desenho que ninguém conferiu contra ele. */
                <code className="meu-numero__qr-texto">{qrNaTela}</code>
              )}
            </div>
          )}

          <div className="meu-numero__acoes">
            {situacao === 'conectado' ? (
              <button
                type="button"
                className="meu-numero__acao meu-numero__acao--perigo"
                disabled={desconexao.isPending}
                onClick={() => {
                  definirConfirmando(true)
                }}
              >
                {desconexao.isPending ? DESCONECTANDO : DESCONECTAR}
              </button>
            ) : (
              <button
                type="button"
                className="meu-numero__acao meu-numero__acao--primaria"
                disabled={pareamento.isPending}
                onClick={() => {
                  pareamento.mutate(undefined, {
                    // O código só substitui o anterior **no sucesso**: com o `503` do provedor, o
                    // QR que a pessoa está lendo continua onde estava, com a recusa por cima.
                    onSuccess: (dados) => {
                      definirQr({ codigo: dados.codigo_qr, expiraEm: dados.expira_em })
                    },
                  })
                }}
              >
                {rotuloDoConectar}
              </button>
            )}
          </div>

          {/* A recusa do serviço, verbatim. `role="alert"` porque ela acabou de acontecer, em
              resposta a um clique — ao contrário da ajuda, que já estava na tela. */}
          {pareamento.isError ? (
            <p className="meu-numero__erro" role="alert">
              {mensagemDe(pareamento.error)}
            </p>
          ) : null}

          {/* A falha da releitura que roda sozinha. Muda, ela deixaria a pessoa esperando por uma
              confirmação que já não vem — e o selo continuaria dizendo "Pareando" sem que nada mais
              estivesse acontecendo. */}
          {consulta.isError ? (
            <p className="meu-numero__erro" role="alert">
              {mensagemDe(consulta.error)}
            </p>
          ) : null}
        </div>
      </div>

      {confirmando ? (
        <Dialogo
          titulo={TITULO_DA_CONFIRMACAO}
          subtitulo={SUBTITULO_DA_CONFIRMACAO}
          rotuloDoConfirmar={desconexao.isPending ? DESCONECTANDO : DESCONECTAR}
          ocupado={desconexao.isPending}
          /* `undefined`, e não `null`: o `Dialogo` desenha a recusa quando o valor **existe**, e o
             `null` de "não houve erro" do TanStack Query cairia dentro daquele ramo. */
          erro={desconexao.error ?? undefined}
          aoFechar={() => {
            definirConfirmando(false)
          }}
          aoConfirmar={() => {
            desconexao.mutate(undefined, {
              // Fecha **só** no sucesso: com o `503` do provedor, o diálogo fica de pé mostrando a
              // recusa. Fechado nos dois, a pessoa veria o cartão continuar "Conectado" sem saber
              // se o clique chegou a acontecer.
              onSuccess: () => {
                definirConfirmando(false)
                // O QR da tentativa anterior perde o sentido: o número saiu do ar, e o próximo
                // pareamento começa do zero.
                definirQr(undefined)
              },
            })
          }}
        />
      ) : null}
    </section>
  )
}
